import React from 'react';

interface Props {
  activeIndex: number;
  slidesCount?: number;
  enabled?: boolean;
  onChange: (index: number) => void;
  orientation: 'horizontal' | 'vertical';
}

export const useGallery = ({ activeIndex = 0, slidesCount = 1, orientation, onChange, enabled = true }: Props) => {
  const isHorizontal = orientation === 'horizontal';
  const ref = React.useRef<HTMLDivElement>(null);
  const [internalIndex, setInternalIndex] = React.useState(activeIndex);

  const getScrollToOptions = React.useCallback(
    (width: number, height: number) => {
      const dimension = isHorizontal ? width / slidesCount : height / slidesCount;

      return isHorizontal ? { left: dimension * activeIndex } : { top: dimension * activeIndex };
    },
    [activeIndex, isHorizontal, slidesCount],
  );

  const handleScroll = React.useCallback(
    (event: Event) => {
      if (!enabled) {
        return;
      }
      const { clientHeight, clientWidth, scrollLeft, scrollTop } = event.target as HTMLDivElement;
      const scrollIndex =
        isHorizontal ?
          Math.max(Math.floor((scrollLeft + clientWidth / slidesCount / 2) / (clientWidth / slidesCount)), 0)
        : Math.max(Math.floor((scrollTop + clientHeight / slidesCount / 2) / (clientHeight / slidesCount)), 0);
      if (scrollIndex !== internalIndex) {
        setInternalIndex(scrollIndex);
        onChange(scrollIndex);
      }
    },
    [internalIndex, isHorizontal, onChange, slidesCount, enabled],
  );

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  React.useEffect(() => {
    if (!enabled || internalIndex === activeIndex) {
      return;
    }

    setInternalIndex(activeIndex);
    const { width = 0, height = 0 } = ref.current?.getBoundingClientRect() ?? {};

    const scrollToOptions = getScrollToOptions(width, height);
    ref.current?.scrollTo(scrollToOptions);
  }, [activeIndex, internalIndex, getScrollToOptions, isHorizontal, enabled]);

  return { ref };
};
